import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';

import DateBadge from '@/components/deal/delivery-date/date-badge';
import { ComboboxAndPopover } from '@/components/ui/combobox/combobox-and-popover';
import { Combocal } from '@/components/ui/combocal/combocal';
import InlineTextInput from '@/components/ui/inline-text-input';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { HOTKEYS } from '@/lib/hotkeys';
import { cn } from '@/lib/utils';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { debounce } from 'lodash';

dayjs.extend(customParseFormat);

export const DATE_FORMAT = 'MMM D, YYYY';
export const DATE_FORMAT_NO_YEAR = 'MMM D';
export const FRIENDLY_EDITABLE_DATE_FORMAT = 'M/D/YYYY';

export type DateSelectorPopoverProps = {
  date: Date | string | undefined | null;
  hideIcon?: boolean;
  hideYear?: boolean;
  dateClassName?: string;
  side?: 'left' | 'top' | 'right' | 'bottom' | undefined;
  align?: 'center' | 'start' | 'end' | undefined;
  withHotkeyListener?: boolean;
  hotkey?: string | [string, string];
  onSelectDate: (date: Date | undefined) => void;
  withInput?: boolean;
  className?: string;
  icon?: React.ReactNode;
  placeholder?: React.ReactNode;
  inputKey?: string;
};

const DEFAULT_PLACEHOLDER = 'Select Date';

export const DateSelectorPopover = ({
  date,
  hideIcon = false,
  hideYear = false,
  dateClassName,
  side = 'left',
  align = 'start',
  withHotkeyListener = false,
  hotkey,
  onSelectDate,
  withInput = false,
  className,
  icon,
  placeholder = DEFAULT_PLACEHOLDER,
  inputKey = 'date-selector-input',
}: DateSelectorPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(
    date ? dayjs(date).format(FRIENDLY_EDITABLE_DATE_FORMAT) : '',
  );

  const handleClose = () => {
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleSelectDate = (date: Date | undefined) => {
    onSelectDate(date);
    handleClose();
  };

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (date) {
      setInputValue(dayjs(date).format(FRIENDLY_EDITABLE_DATE_FORMAT));
    }
  }, [date]);

  const debouncedOnSelectDate = useCallback(
    debounce((date: Date | undefined) => {
      onSelectDate(date);
    }, 500),
    [onSelectDate],
  );

  return (
    <div className="z-50">
      <ComboboxAndPopover
        combobox={
          <Combocal
            onSelectDate={handleSelectDate}
            date={date ? new Date(date) : undefined}
          ></Combocal>
        }
        className="w-fit"
        side={side}
        align={align}
        open={open}
        setOpen={setOpen}
        onOpenAutoFocus={(e) => {
          if (!withInput) {
            return;
          }

          // UX NOTE: when we have `withInput` we want the input to be focused
          // when the combobox is opened
          // so we need to prevent the default behavior of the combobox
          // from focusing on the combocal
          e.preventDefault();
        }}
      >
        <div
          // DEV NOTE: without this div, combobox-popover won't work
          // that is, clicks will not open the combobox
          // I think the reason is children node needs to be able to receive a ref
          // in order to detect a click event, which makes it open
          // thus so this div is a workaround
          className={className}
        >
          {!withInput && (
            <DateBadge
              date={date?.toString() || ''}
              hideIcon={hideIcon}
              hideYear={hideYear}
              dateClassName={dateClassName}
              placeholder={placeholder}
              icon={icon}
            />
          )}
          {withInput && (
            <InlineTextInput
              startAdornment={
                icon && (
                  <div className={cn('h-3.5 w-3.5 cursor-pointer mr-1')}>
                    {icon}
                  </div>
                )
              }
              key={inputKey}
              placeholder={
                typeof placeholder === 'string'
                  ? placeholder
                  : DEFAULT_PLACEHOLDER
              }
              value={inputValue}
              wrapperClassName="w-full"
              className="w-full! !text-xs !text-gray-500 "
              ref={inputRef}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const value = event.target.value;

                setInputValue(value);

                const parsedDate = dayjs(value, FRIENDLY_EDITABLE_DATE_FORMAT);

                if (!parsedDate.isValid()) {
                  return;
                }

                const valueToDate = parsedDate.toDate();
                debouncedOnSelectDate(valueToDate);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleClose();
                }

                if (event.key === HOTKEYS.GLOBAL_UNFOCUS_OR_CLOSE) {
                  event.preventDefault();
                  handleClose();
                }
              }}
              onBlur={() => {
                // console.log('DateSelectorPopover onBlur');
              }}
            />
          )}
        </div>
      </ComboboxAndPopover>
      {withHotkeyListener && hotkey && (
        <HotKeyListener
          hotkeys={hotkey}
          onHotkeyPressed={() => {
            setOpen(true);
          }}
        />
      )}
    </div>
  );
};
